'use client'

import { useMutation } from '@apollo/client'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input
} from '@chain-zen/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import * as z from 'zod'

import { UPDATE_TRUCKER_TOOLS_CARRIER_APP } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import { useAuth } from '../../../hooks/use-auth'
import type { TCarrier } from '../../../types/apps'
import type { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'

const formSchema = z.object({
  carrierIds: z
    .array(
      z
        .object({
          brokerMC: z.string().min(1, 'Broker MC Number is required'),
          dotNumber: z.string().min(1, 'Carrier DOT Number is required'),
          integrationConfigurationID: z.string().optional(),
          mcNumber: z.string().min(1, 'MC Number is required'),
          overrideBrokerMC: z.string().optional(),
          overrideDotNumber: z.string().optional(),
          overrideMcNumber: z.string().optional(),
          overrideScacCode: z.string().optional(),
          provider: z.string().min(1, 'Provider is required'),
          scacCode: z.string().min(1, 'SCAC Code is required'),
          useCustomTruckerToolsDetails: z.boolean().optional()
        })
        .superRefine((carrier, context) => {
          if (!carrier.useCustomTruckerToolsDetails) {
            return
          }

          if (!carrier.overrideBrokerMC?.trim()) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Broker MC (Trucker Tools Override) is required',
              path: ['overrideBrokerMC']
            })
          }

          if (!carrier.overrideDotNumber?.trim()) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Carrier DOT (Trucker Tools Override) is required',
              path: ['overrideDotNumber']
            })
          }

          if (!carrier.overrideMcNumber?.trim()) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Carrier MC (Trucker Tools Override) is required',
              path: ['overrideMcNumber']
            })
          }

          if (!carrier.overrideScacCode?.trim()) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'SCAC Code (Trucker Tools Override) is required',
              path: ['overrideScacCode']
            })
          }
        })
    )
    .min(1, 'At least one carrier is required')
})

type TFormValues = z.infer<typeof formSchema>
type TFormCarrier = TFormValues['carrierIds'][number]

type TTruckerToolsCarrierUpdateInput = {
  appType: AppTypeEnum
  updates: { created: TCarrier[]; deleted: TCarrier[]; updated: TCarrier[] }
}

const getNormalizedCarrier = (carrier: TCarrier): Omit<TCarrier, 'integrationConfigurationID'> => {
  const normalizedCarrier = {
    brokerMC: carrier.brokerMC.trim(),
    dotNumber: carrier.dotNumber.trim(),
    mcNumber: carrier.mcNumber.trim(),
    provider: carrier.provider.trim(),
    scacCode: carrier.scacCode.trim()
  }

  if (!carrier.truckerToolsOverride) {
    return normalizedCarrier
  }

  return {
    ...normalizedCarrier,
    truckerToolsOverride: {
      brokerMC: carrier.truckerToolsOverride.brokerMC.trim(),
      dotNumber: carrier.truckerToolsOverride.dotNumber.trim(),
      mcNumber: carrier.truckerToolsOverride.mcNumber.trim(),
      scacCode: carrier.truckerToolsOverride.scacCode.trim()
    }
  }
}

const isSameCarrierPayload = (firstCarrier: TCarrier, secondCarrier: TCarrier): boolean => {
  return JSON.stringify(getNormalizedCarrier(firstCarrier)) === JSON.stringify(getNormalizedCarrier(secondCarrier))
}

const mapFormCarrierToPayload = (carrier: TFormCarrier): TCarrier => {
  const payload: TCarrier = {
    brokerMC: carrier.brokerMC.trim(),
    dotNumber: carrier.dotNumber.trim(),
    integrationConfigurationID: carrier.integrationConfigurationID,
    mcNumber: carrier.mcNumber.trim(),
    provider: carrier.provider.trim(),
    scacCode: carrier.scacCode.trim()
  }

  if (
    carrier.useCustomTruckerToolsDetails &&
    carrier.overrideBrokerMC?.trim() &&
    carrier.overrideDotNumber?.trim() &&
    carrier.overrideMcNumber?.trim() &&
    carrier.overrideScacCode?.trim()
  ) {
    payload.truckerToolsOverride = {
      brokerMC: carrier.overrideBrokerMC.trim(),
      dotNumber: carrier.overrideDotNumber.trim(),
      mcNumber: carrier.overrideMcNumber.trim(),
      scacCode: carrier.overrideScacCode.trim()
    }
  }

  return payload
}

const findMatchingCarrier = (existingCarriers: TCarrier[], carrier: TCarrier): TCarrier | undefined => {
  return existingCarriers.find((existingCarrier) => {
    if (existingCarrier.integrationConfigurationID && carrier.integrationConfigurationID) {
      return existingCarrier.integrationConfigurationID === carrier.integrationConfigurationID
    }

    return existingCarrier.mcNumber === carrier.mcNumber && existingCarrier.scacCode === carrier.scacCode
  })
}

export function TruckerToolsCarrierUpdateModal({ isOpen, onClose, appType, app }: AppModalProps) {
  const { selectedWorkspace } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const [formError, setFormError] = useState('')
  const existingCarriers: TCarrier[] = (app?.workspaceApp?.appMetadata?.carriers || []).map((carrier) => ({
    brokerMC: carrier.brokerMC ?? '',
    dotNumber: carrier.dotNumber ?? '',
    integrationConfigurationID: carrier.integrationConfigurationID,
    mcNumber: carrier.mcNumber ?? '',
    provider: carrier.provider ?? '',
    scacCode: carrier.scacCode ?? '',
    ...(carrier.truckerToolsOverride
      ? {
          truckerToolsOverride: {
            brokerMC: carrier.truckerToolsOverride.brokerMC ?? '',
            dotNumber: carrier.truckerToolsOverride.dotNumber ?? '',
            mcNumber: carrier.truckerToolsOverride.mcNumber ?? '',
            scacCode: carrier.truckerToolsOverride.scacCode ?? ''
          }
        }
      : {})
  }))
  const deletedCarriersRef = useRef<TCarrier[]>([])

  const initialCarriers =
    existingCarriers.length > 0
      ? existingCarriers.map((carrier) => ({
          brokerMC: carrier.brokerMC,
          dotNumber: carrier.dotNumber,
          integrationConfigurationID: carrier.integrationConfigurationID,
          mcNumber: carrier.mcNumber,
          overrideBrokerMC: carrier.truckerToolsOverride?.brokerMC ?? '',
          overrideDotNumber: carrier.truckerToolsOverride?.dotNumber ?? '',
          overrideMcNumber: carrier.truckerToolsOverride?.mcNumber ?? '',
          overrideScacCode: carrier.truckerToolsOverride?.scacCode ?? '',
          provider: carrier.provider,
          scacCode: carrier.scacCode,
          useCustomTruckerToolsDetails: !!carrier.truckerToolsOverride
        }))
      : [
          {
            brokerMC: '',
            dotNumber: '',
            integrationConfigurationID: undefined as string | undefined,
            mcNumber: '',
            overrideBrokerMC: '',
            overrideDotNumber: '',
            overrideMcNumber: '',
            overrideScacCode: '',
            provider: '',
            scacCode: '',
            useCustomTruckerToolsDetails: false
          }
        ]

  const form = useForm<TFormValues>({
    defaultValues: {
      carrierIds: initialCarriers
    },
    resolver: zodResolver(formSchema)
  })

  const [updateTruckerToolsCarrierApp, { loading }] = useMutation<
    { updateCarriersInTruckerToolsCarrier: { id: string } },
    TTruckerToolsCarrierUpdateInput
  >(UPDATE_TRUCKER_TOOLS_CARRIER_APP, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    onCompleted: () => {
      toast.success('Trucker Tools Carrier app is updated successfully!')
      handleClose()
    },
    onError: (err) => {
      const errorMessage = `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
      setFormError(errorMessage)
      toast.error(errorMessage)
    },
    refetchQueries: [GET_APPS]
  })

  const handleClose = () => {
    setFormError('')
    form.reset()
    deletedCarriersRef.current = []
    onClose()
  }

  const addCarrier = () => {
    const currentCarriers = form.getValues('carrierIds')
    const newCarrier = {
      brokerMC: '',
      dotNumber: '',
      integrationConfigurationID: undefined as string | undefined,
      mcNumber: '',
      overrideBrokerMC: '',
      overrideDotNumber: '',
      overrideMcNumber: '',
      overrideScacCode: '',
      provider: '',
      scacCode: '',
      useCustomTruckerToolsDetails: false
    }
    form.setValue('carrierIds', [...currentCarriers, newCarrier])
  }

  const removeCarrier = (index: number) => {
    const currentCarriers = form.getValues('carrierIds')
    if (currentCarriers.length > 1) {
      const carrierToRemove = currentCarriers[index]
      if (carrierToRemove.integrationConfigurationID) {
        deletedCarriersRef.current.push(mapFormCarrierToPayload(carrierToRemove))
      }
      form.setValue(
        'carrierIds',
        currentCarriers.filter((_, i) => i !== index)
      )
    }
  }

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    const payloadCarriers = values.carrierIds.map((carrier) => mapFormCarrierToPayload(carrier))

    const createdCarriers = payloadCarriers.filter((carrier) => !carrier.integrationConfigurationID)
    const updatedCarriers = payloadCarriers.filter((carrier) => {
      if (!carrier.integrationConfigurationID) {
        return false
      }

      const existingCarrier = findMatchingCarrier(existingCarriers, carrier)

      if (!existingCarrier) {
        return true
      }

      return !isSameCarrierPayload(existingCarrier, carrier)
    })

    updateTruckerToolsCarrierApp({
      variables: {
        appType,
        updates: {
          created: createdCarriers,
          deleted: deletedCarriersRef.current,
          updated: updatedCarriers
        }
      }
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg w-full max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Update Trucker Tools Carrier for Chain</DialogTitle>
          <DialogDescription>Update the carrier details for your Trucker Tools integration.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {form.watch('carrierIds').map((carrier, index) => (
              <div
                key={carrier.integrationConfigurationID || index}
                className="space-y-4 rounded-lg bg-gray-50 p-4 dark:bg-gray-900"
              >
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium">Carrier {index + 1}</h4>
                  {form.watch('carrierIds').length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeCarrier(index)}
                      disabled={loading}
                    >
                      Remove
                    </Button>
                  )}
                </div>

                <FormField
                  control={form.control}
                  name={`carrierIds.${index}.dotNumber`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Carrier DOT Number</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter carrier DOT number" {...field} disabled={loading} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`carrierIds.${index}.mcNumber`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>MC Number</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter MC number" {...field} disabled={loading} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`carrierIds.${index}.scacCode`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>SCAC Code</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter SCAC code" {...field} disabled={loading} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`carrierIds.${index}.provider`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Provider</FormLabel>
                      <p className="text-xs text-muted-foreground mb-1">
                        This value is shared with the Trucker Tools team for load tracking identification.
                      </p>
                      <FormControl>
                        <Input placeholder="Enter provider (from Trucker Tools team)" {...field} disabled={loading} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`carrierIds.${index}.brokerMC`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Broker MC Number</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter broker MC number" {...field} disabled={loading} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="border rounded-md px-3 py-2">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <FormLabel>Use different Trucker Tools details</FormLabel>
                      <p className="text-xs text-muted-foreground">
                        Optional: override outbound carrier and broker details sent to Trucker Tools.
                      </p>
                    </div>
                    <Input
                      id={`carrierIds.${index}.useCustomTruckerToolsDetails`}
                      type="checkbox"
                      className="h-4 w-4 mt-1"
                      checked={carrier.useCustomTruckerToolsDetails ?? false}
                      disabled={loading}
                      onChange={(event) => {
                        const enabled = event.target.checked
                        form.setValue(`carrierIds.${index}.useCustomTruckerToolsDetails`, enabled)
                        if (!enabled) {
                          form.setValue(`carrierIds.${index}.overrideBrokerMC`, '')
                          form.setValue(`carrierIds.${index}.overrideDotNumber`, '')
                          form.setValue(`carrierIds.${index}.overrideMcNumber`, '')
                          form.setValue(`carrierIds.${index}.overrideScacCode`, '')
                        }
                      }}
                    />
                  </div>

                  {carrier.useCustomTruckerToolsDetails ? (
                    <div className="mt-3 space-y-3">
                      <FormField
                        control={form.control}
                        name={`carrierIds.${index}.overrideDotNumber`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Carrier DOT (Trucker Tools Override)</FormLabel>
                            <FormControl>
                              <Input placeholder="Enter carrier DOT override" {...field} disabled={loading} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name={`carrierIds.${index}.overrideMcNumber`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Carrier MC (Trucker Tools Override)</FormLabel>
                            <FormControl>
                              <Input placeholder="Enter carrier MC override" {...field} disabled={loading} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name={`carrierIds.${index}.overrideScacCode`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>SCAC Code (Trucker Tools Override)</FormLabel>
                            <FormControl>
                              <Input placeholder="Enter SCAC override" {...field} disabled={loading} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name={`carrierIds.${index}.overrideBrokerMC`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Broker MC (Trucker Tools Override)</FormLabel>
                            <FormControl>
                              <Input placeholder="Enter broker MC override" {...field} disabled={loading} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            ))}

            <Button type="button" variant="outline" onClick={addCarrier} disabled={loading}>
              Add Another Carrier
            </Button>

            {!!formError && <Alert variant="error" description={formError} />}

            <DialogFooter className="flex flex-col gap-4 sm:flex-row sm:justify-end sm:gap-0">
              <Button variant="outline" onClick={handleClose} type="button">
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Updating...' : 'Update'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
